import { useEffect, useRef, useState } from "react"
import Spinner from "@/components/Spinner/Spinner"
import { useTick } from "@/hooks/useTick"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import {
  adjustTimer,
  pauseTimer,
  resumeTimer,
  startTimer,
  updateSessionConfig,
} from "@/services/sessions"
import { useSessionStore } from "@/store/sessionStore"
import { selectRound, selectSession, selectTimer } from "@/store/selectors"
import type { Round, TimerState } from "@/types"
import { primeChime } from "@/utils/chime"
import { formatDuration, isTimerExpired, minutesToMs, timerRemainingMs } from "@/utils/timer"
import styles from "@/styles/components/SessionTimer.module.css"

/*
 * The room's clock.
 *
 * One timer, on the session document, so the number on the wall projector and
 * the number in someone's hand are the same number. Nothing here counts: the
 * remaining time is recomputed from `timer.startedAt` on every render and
 * `useTick` only supplies the renders.
 *
 * There is exactly one clock that is GOING — `counting`: running, and not yet
 * past zero. `timer.status` stays `running` after a round runs out, because
 * nobody is awake at that instant to write a status, so "is it going" is always
 * status AND `!isTimerExpired`. Everything else — never started, paused, run out
 * — is a clock the room is not watching tick, and the button beside it says play.
 *
 * That single distinction drives both controls:
 *
 *   - Play is four writes, chosen from what there is to play. A going clock
 *     pauses. A paused one resumes (`startTimer` always restarts from zero, so
 *     resuming through it would hand the round its elapsed time back). One that
 *     is idle or has RUN OUT has nothing to carry on with, so play offers a fresh
 *     clock instead of pausing something that already stopped.
 *   - ± NEVER writes on the press. While the clock is going, a run of presses is
 *     accumulated locally and committed as ONE `adjustTimer` once they stop;
 *     while it is not, they are a draft of the clock the play button would start
 *     or resume, and PLAY is what commits them. Retiming a round is a decision
 *     made in three or four presses, and each of them landing on the session
 *     document put the half-finished number on every screen in the room.
 *
 * The number under the buttons always includes presses that have not been
 * committed yet — that is what makes the wait invisible — and it keeps including
 * a committed run until the room's clock actually catches up. `adjustTimer` is a
 * transaction, and a transaction has no local application at all: dropping the
 * delta when the write went out flashed the OLD number for the whole server
 * round trip. `sameClock` is how this screen knows the answer has landed.
 */

const TIMER_COPY = {
  start: "Start the clock",
  pause: "Pause the clock",
  resume: "Resume the clock",
  restart: "Start a new clock",
  addMinute: "Add a minute",
  removeMinute: "Take a minute off",
  overtime: "Over",
  wrapUp: "Wrap up",
} as const

const MIN_MINUTES = 1
const MAX_MINUTES = 60

/**
 * Quiet time before a run of ± presses on a going clock is committed.
 *
 * Long enough to cover the gap between two deliberate presses on the same
 * button, short enough that a single press is taken while the finger is still
 * near it. The clock has already moved, so what waits here is only the write.
 */
const ADJUST_DEBOUNCE_MS = 700

/*
 * One key per control, so pressing + does not put a spinner on the play button.
 * Both ± presses commit through the same write, so they share one key: the
 * spinner belongs to the queued total, not to whichever glyph was pressed last.
 */
const TOGGLE = "toggle"
const ADJUST = "adjust"

/**
 * A run of ± presses that has been written, and the clock it was written
 * against. It keeps reading on screen until the room's clock catches up.
 *
 * `delta` for time added to a clock that already exists, `length` for the whole
 * clock a start is about to create. A start is two writes — the plan, then the
 * timer — and the plan is echoed first, so a delta measured off `planned` would
 * be added to the number it had already become and show a minute that was never
 * asked for. A length is the answer to both writes and cannot drift.
 */
type AppliedAdjust = {
  kind: "delta" | "length"
  minutes: number
  basis: TimerState
}

/**
 * Whether the room's clock is still the one a pending adjustment was measured
 * from. Compared by value, not by object identity: any session write hands the
 * store a new session object, and dropping the delta because someone renamed
 * the customer would flash exactly the number this is here to prevent.
 */
function sameClock(a: TimerState, b: TimerState): boolean {
  return (
    a.status === b.status &&
    a.durationMs === b.durationMs &&
    a.elapsedMs === b.elapsedMs &&
    a.startedAt === b.startedAt
  )
}

interface SessionTimerProps {
  canDrive: boolean
  /** The round is inside its last two minutes — `useWrapUpAlert`. Owned by
   *  `RoundControls`, which already holds the alert hooks, and rendered here
   *  because only this component knows where the digits are. */
  wrapUp?: boolean
  /** The beat it crossed, which is when the word shoves. */
  wrapUpAlerting?: boolean
}

export default function SessionTimer({ canDrive, wrapUp, wrapUpAlerting }: SessionTimerProps) {
  const session = useSessionStore(selectSession)
  const timer = useSessionStore(selectTimer)
  const round = useSessionStore(selectRound)

  if (!session || !timer || !round) return null

  /*
   * The controls live in their own component so their state can be held in
   * unconditional hooks below this guard: they keep local presses that must
   * survive a snapshot, and a component that returns before its hooks cannot
   * have any.
   *
   * Keyed by round, which is how the presses are forgotten when the room moves.
   * Every position write resets the clock, so a draft aimed at the round the
   * room has left has nothing to change — and a remount is one line rather than
   * an effect that clears three pieces of state a render late.
   */
  return (
    <Clock
      key={round}
      sessionId={session.id}
      round={round}
      timer={timer}
      planned={session.config.roundDurations[round] ?? 5}
      canDrive={canDrive}
      wrapUp={wrapUp}
      wrapUpAlerting={wrapUpAlerting}
    />
  )
}

interface ClockProps {
  sessionId: string
  round: Round
  timer: TimerState
  /** The round's planned length in minutes — what a fresh clock starts from. */
  planned: number
  canDrive: boolean
  wrapUp?: boolean
  wrapUpAlerting?: boolean
}

function Clock({ sessionId, round, timer, planned, canDrive, wrapUp, wrapUpAlerting }: ClockProps) {
  const { write, isPending } = useFirestoreWrite()

  // Only a going clock needs a heartbeat. Idle, paused and over are static.
  const now = useTick(timer.status === "running")

  /** Presses not yet written, in minutes, signed. */
  const [queued, setQueued] = useState(0)
  /**
   * The queued run was pressed against a clock that was GOING at the time.
   *
   * A minute added in a round's last breath belongs to that round. Without this
   * the clock crosses zero inside the debounce, the commit is dropped as "not
   * counting", and the presses are silently re-read as the plan for a clock the
   * facilitator never asked to restart — leaving them to press Start on time
   * they had already added.
   */
  const [queuedExtends, setQueuedExtends] = useState(false)
  /** The run that has been written and is waiting for the room's clock. */
  const [applied, setApplied] = useState<AppliedAdjust | null>(null)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  /*
   * A written run still stands until the clock it was measured from changes.
   * Derived at render rather than cleared by an effect, so the frame the
   * snapshot arrives on is already right: an effect is always one render late,
   * and one render of the old number is the flash this whole mechanism exists to
   * stop. Nothing has to clear it either — `startedAt` is a fresh server stamp
   * every time, so a clock that has moved on can never match a basis again.
   *
   * Only one of the two is ever non-zero: no press is taken while a written run
   * is still standing (see `busy`), so a queued run always starts from a clock
   * this screen has actually seen.
   */
  const held = applied !== null && sameClock(applied.basis, timer) ? applied : null

  const expired = isTimerExpired(timer, now)
  /** Time on its way to the clock the document still says is running. */
  const extending = timer.status === "running" && (queuedExtends || held?.kind === "delta")
  /*
   * The one clock that is GOING — and an extension keeps it going across zero,
   * because the round has not actually run out while the minute that lengthens
   * it is still in this screen's hands.
   */
  const counting = timer.status === "running" && (!expired || extending)
  /** Nothing to carry on with: play would start a new clock of `planned`. */
  const fresh = (timer.status === "idle" || expired) && !counting

  /** The clock a start already out is about to become. */
  const starting = held?.kind === "length" ? held.minutes : null
  const pending = starting !== null ? 0 : queued + (held?.kind === "delta" ? held.minutes : 0)

  /** What a fresh clock would run for, in minutes. */
  const draftMinutes = planned + pending

  /*
   * An idle clock shows the length it would run, a going or paused one shows
   * what is left of it, and one that has run out shows 0:00 — until a press
   * turns it into the plan for the clock that would replace it.
   */
  const drafting = starting === null && fresh && (timer.status === "idle" || pending !== 0)
  const remaining =
    starting !== null
      ? minutesToMs(starting)
      : drafting
        ? minutesToMs(draftMinutes)
        : Math.max(timerRemainingMs(timer, now) + minutesToMs(pending), 0)
  const over = expired && !counting && starting === null && !drafting

  /*
   * The debounce fires long after the render that scheduled it, so it goes
   * through a ref that every render refreshes rather than closing over the
   * presses as they stood at the first one.
   */
  const commitRef = useRef<() => void>(() => {})

  const stopDebounce = () => {
    if (timeout.current !== null) {
      clearTimeout(timeout.current)
      timeout.current = null
    }
  }

  /**
   * Hands the queued run to a write, and gives back the way to disown it.
   *
   * `asLength` is the whole clock a start is about to create; it is held even
   * when nothing was queued, so play on a round that has run out reads as the
   * new clock rather than sitting on 0:00 for the round trip.
   */
  const take = (asLength: number | null) => {
    stopDebounce()
    setQueuedExtends(false)
    if (queued === 0 && asLength === null) return { minutes: 0, forget: () => {} }
    const entry: AppliedAdjust =
      asLength !== null
        ? { kind: "length", minutes: asLength, basis: timer }
        : { kind: "delta", minutes: queued, basis: timer }
    const minutes = queued
    setQueued(0)
    setApplied(entry)
    // A refused write means the clock never moved, so the number has to come
    // back down — but only if this is still the run on screen.
    return {
      minutes,
      forget: () => setApplied((current) => (current === entry ? null : current)),
    }
  }

  const commitQueued = () => {
    /*
     * Only a going clock commits on its own; every other state is waiting for
     * play, which takes the queued run with it. A clock that crossed zero inside
     * the debounce is still going as far as this is concerned — the presses were
     * made against it, and `counting` says so.
     */
    if (!counting) {
      stopDebounce()
      return
    }
    const { minutes, forget } = take(null)
    if (minutes === 0) return
    write(async () => {
      try {
        await adjustTimer(sessionId, minutesToMs(minutes))
      } catch (error) {
        forget()
        throw error
      }
    }, ADJUST)
  }

  useEffect(() => {
    commitRef.current = commitQueued
  })

  /*
   * Commit on unmount too. The presses are a decision the facilitator has
   * already watched land on their own clock; dropping them because the bar was
   * swapped out inside the debounce would silently undo it.
   */
  useEffect(() => () => commitRef.current(), [])

  const toggle = () => {
    /* The one gesture that reliably precedes the bell by minutes. Audio cannot
       be unlocked from the timer callback that rings it — see `primeChime`. */
    primeChime()
    if (counting) {
      const { minutes, forget } = take(null)
      write(async () => {
        try {
          // Queued presses travel with the pause rather than racing it: the
          // facilitator is confirming the number they can see.
          if (minutes !== 0) await adjustTimer(sessionId, minutesToMs(minutes))
          await pauseTimer(sessionId)
        } catch (error) {
          forget()
          throw error
        }
      }, TOGGLE)
      return
    }

    if (!fresh) {
      // A paused clock is where the draft is committed: nothing was written
      // while it stood still, so resuming carries the presses with it.
      const { minutes, forget } = take(null)
      write(async () => {
        try {
          if (minutes !== 0) await adjustTimer(sessionId, minutesToMs(minutes))
          await resumeTimer(sessionId)
        } catch (error) {
          forget()
          throw error
        }
      }, TOGGLE)
      return
    }

    const startMinutes = draftMinutes
    const { forget } = take(startMinutes)
    write(async () => {
      try {
        // The plan follows the draft, so stepping back into this round later
        // finds the length the facilitator chose rather than the one they
        // replaced. It is also the length the NEXT fresh clock starts from.
        if (startMinutes !== planned) {
          await updateSessionConfig(sessionId, { roundDurations: { [round]: startMinutes } })
        }
        await startTimer(sessionId, minutesToMs(startMinutes))
      } catch (error) {
        forget()
        throw error
      }
    }, TOGGLE)
  }

  const adjust = (deltaMinutes: number) => {
    // A reload mid-round leaves a clock nobody pressed play on; ± is the other
    // gesture that lands on it before it runs out.
    primeChime()
    if (fresh) {
      // Clamped against the plan, since this is the whole length of a clock
      // rather than an addition to one already running.
      const next = Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, draftMinutes + deltaMinutes))
      setQueued(next - planned)
      return
    }

    setQueued(queued + deltaMinutes)
    // A paused clock has no debounce: play is what commits it.
    if (!counting) return
    setQueuedExtends(true)
    if (timeout.current !== null) clearTimeout(timeout.current)
    timeout.current = setTimeout(() => commitRef.current(), ADJUST_DEBOUNCE_MS)
  }

  /*
   * A control is refused exactly while the clock it edits is known to be stale:
   * its own write is out, or the answer to one has not come back yet. Pause,
   * resume and start are three different writes chosen from the CURRENT clock,
   * so a press against one that is already changing picks the wrong branch —
   * pausing a clock the room has just watched start. A held run outlasts
   * `isPending` by the beat between the server answering and the listener
   * delivering, and both halves of that beat are stale. The debounce is longer
   * than the trip, so a burst of presses never meets this.
   */
  const toggling = isPending(TOGGLE)
  /** Something is out, or its answer has not landed: nothing may be pressed. */
  const busy = toggling || isPending(ADJUST) || held !== null
  /** ± wear the spinner only for their OWN write, not for a start or a pause. */
  const adjusting = isPending(ADJUST) || held?.kind === "delta"

  // A going or paused clock can always be lengthened; shortening stops at the
  // minute the round has already used, which `adjustTimer` clamps to.
  const canRemove = fresh ? draftMinutes > MIN_MINUTES : remaining > minutesToMs(1)
  const canAdd = fresh ? draftMinutes < MAX_MINUTES : true

  const toggleLabel = counting
    ? TIMER_COPY.pause
    : expired
      ? TIMER_COPY.restart
      : timer.status === "paused"
        ? TIMER_COPY.resume
        : TIMER_COPY.start

  /*
   * The round's last two minutes, under the digits.
   *
   * Taken out of flow and hung off whichever element the digits are centred in
   * — the ± group for the facilitator, the whole readout for everyone else. In
   * flow it would centre under the CONTROLS, and the play button to the left
   * drags that midpoint off the numbers. The space it drops into is reserved by
   * a permanent `padding-bottom`, so the capsule never changes height.
   */
  const wrapUpLabel = wrapUp ? (
    <span
      className={wrapUpAlerting ? `${styles.wrapUp} ${styles.wrapUpShake}` : styles.wrapUp}
      role="status"
    >
      {TIMER_COPY.wrapUp}
    </span>
  ) : null

  const clock = (
    <span
      className={over ? `${styles.clock} ${styles.over}` : styles.clock}
      role="timer"
      aria-live="off"
    >
      {formatDuration(remaining)}
    </span>
  )

  if (!canDrive) {
    return (
      <span className={styles.readOnly}>
        {clock}
        {over ? <span className={styles.overLabel}>{TIMER_COPY.overtime}</span> : null}
        {wrapUpLabel}
      </span>
    )
  }

  return (
    <span className={styles.timer}>
      <button
        type="button"
        className={styles.play}
        title={toggleLabel}
        aria-label={toggleLabel}
        disabled={busy}
        aria-busy={toggling || undefined}
        onClick={toggle}
      >
        {toggling ? (
          <Spinner size="sm" />
        ) : counting ? (
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <rect x="2" y="1.5" width="3" height="9" rx="1" fill="currentColor" />
            <rect x="7" y="1.5" width="3" height="9" rx="1" fill="currentColor" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M3 1.8 10 6 3 10.2Z" fill="currentColor" />
          </svg>
        )}
      </button>

      <span className={styles.adjust}>
        {/* Free while the presses are only queued — that is most of the time, and
         * retiming a round is a burst of them — and refused for the round trip
         * that commits them, because the next press has to be measured from a
         * clock this screen has seen. The debounce is longer than the trip, so a
         * burst never meets it. */}
        <button
          type="button"
          className={styles.step}
          title={TIMER_COPY.removeMinute}
          aria-label={TIMER_COPY.removeMinute}
          disabled={!canRemove || busy}
          aria-busy={adjusting || undefined}
          onClick={() => adjust(-1)}
        >
          {adjusting ? <Spinner size="sm" /> : "−"}
        </button>
        {clock}
        <button
          type="button"
          className={styles.step}
          title={TIMER_COPY.addMinute}
          aria-label={TIMER_COPY.addMinute}
          disabled={!canAdd || busy}
          aria-busy={adjusting || undefined}
          onClick={() => adjust(1)}
        >
          {adjusting ? <Spinner size="sm" /> : "+"}
        </button>
        {wrapUpLabel}
      </span>
    </span>
  )
}
